import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { apiFetch, supabase, isAdmin } from '../lib/supabase';
import { formatApiError, profileReminderSessionKey } from '../lib/onboarding';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

const unavailableProfileStatus = {
  available: false,
  complete: false,
  reminder_required: false,
  reason: 'status_unavailable',
  profile: null,
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdminUser, setIsAdminUser] = useState(false);
  const [profileStatus, setProfileStatus] = useState(null);
  const [profileStatusUserId, setProfileStatusUserId] = useState(null);
  const [profileStatusLoading, setProfileStatusLoading] = useState(false);
  const [profileReminderDismissed, setProfileReminderDismissed] = useState(false);
  const reminderKeyRef = useRef(null);
  const activeUserIdRef = useRef(null);
  const activeSessionKeyRef = useRef(null);
  const sessionEpochRef = useRef(0);
  const latestProfileRequestRef = useRef(0);
  const authEventVersionRef = useRef(0);
  const bootRequestRef = useRef(0);
  const mountedRef = useRef(true);

  const requestProfileStatus = useCallback(async (requestUserId, requestEpoch) => {
    if (!requestUserId) return null;
    const requestId = ++latestProfileRequestRef.current;
    const isCurrentRequest = () => (
      mountedRef.current
      && activeUserIdRef.current === requestUserId
      && sessionEpochRef.current === requestEpoch
      && latestProfileRequestRef.current === requestId
    );
    if (isCurrentRequest()) setProfileStatusLoading(true);
    try {
      const response = await apiFetch(`${API_URL}/api/profile/status`);
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(formatApiError(body.detail));
      if (!isCurrentRequest()) return null;
      setProfileStatus(body);
      setProfileStatusUserId(requestUserId);
      return body;
    } catch (_error) {
      if (!isCurrentRequest()) return null;
      setProfileStatus(unavailableProfileStatus);
      setProfileStatusUserId(requestUserId);
      return unavailableProfileStatus;
    } finally {
      if (isCurrentRequest()) setProfileStatusLoading(false);
    }
  }, []);

  const applySession = useCallback((session) => {
    const nextUser = session?.user ?? null;
    const nextUserId = nextUser?.id ?? null;
    const nextSessionKey = nextUser
      ? (profileReminderSessionKey(session) || `${nextUserId}:${session?.access_token || ''}`)
      : null;
    const userChanged = nextUserId !== activeUserIdRef.current;
    const sessionChanged = nextSessionKey !== activeSessionKeyRef.current;

    if (userChanged || sessionChanged) {
      activeUserIdRef.current = nextUserId;
      activeSessionKeyRef.current = nextSessionKey;
      sessionEpochRef.current += 1;
      latestProfileRequestRef.current += 1;
      setProfileStatusLoading(false);
      if (userChanged || !nextUserId) {
        setProfileStatus(null);
        setProfileStatusUserId(null);
      }
    }

    setUser(nextUser);
    setIsAdminUser(Boolean(nextUser?.email && isAdmin(nextUser.email)));
    if (!nextUser) {
      reminderKeyRef.current = null;
      setProfileStatus(null);
      setProfileStatusUserId(null);
      setProfileStatusLoading(false);
      setProfileReminderDismissed(false);
      return;
    }

    const reminderKey = profileReminderSessionKey(session);
    reminderKeyRef.current = reminderKey;
    setProfileReminderDismissed(
      Boolean(reminderKey && window.sessionStorage.getItem(reminderKey) === 'dismissed')
    );
    void requestProfileStatus(nextUserId, sessionEpochRef.current);
  }, [requestProfileStatus]);

  useEffect(() => {
    mountedRef.current = true;
    let disposed = false;
    const bootRequest = ++bootRequestRef.current;
    const authVersionAtBoot = authEventVersionRef.current;
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (disposed) return;
      authEventVersionRef.current += 1;
      applySession(session);
      setLoading(false);
    });

    supabase.auth.getSession()
      .then(({ data, error }) => {
        if (
          disposed
          || error
          || bootRequest !== bootRequestRef.current
          || authEventVersionRef.current !== authVersionAtBoot
        ) return;
        applySession(data?.session ?? null);
        setLoading(false);
      })
      .catch(() => {
        if (!disposed && authEventVersionRef.current === authVersionAtBoot) setLoading(false);
      });

    return () => {
      disposed = true;
      mountedRef.current = false;
      subscription.unsubscribe();
    };
  }, [applySession]);

  const refreshProfileStatus = useCallback(() => (
    requestProfileStatus(activeUserIdRef.current, sessionEpochRef.current)
  ), [requestProfileStatus]);

  const signUp = async (payload) => {
    try {
      const response = await fetch(`${API_URL}/api/onboarding/signup`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) return { data: null, error: new Error(formatApiError(body.detail)) };
      return { data: body, error: null };
    } catch (_error) {
      return { data: null, error: new Error('Signup service is temporarily unavailable.') };
    }
  };

  const signIn = async (email, password) => {
    const result = await supabase.auth.signInWithPassword({ email, password });
    if (result.data?.session) {
      const key = profileReminderSessionKey(result.data.session);
      if (key) window.sessionStorage.removeItem(key);
      setProfileReminderDismissed(false);
      authEventVersionRef.current += 1;
      applySession(result.data.session);
      setLoading(false);
    }
    return result;
  };

  const dismissProfileReminder = useCallback(() => {
    if (reminderKeyRef.current) {
      window.sessionStorage.setItem(reminderKeyRef.current, 'dismissed');
    }
    setProfileReminderDismissed(true);
  }, []);

  const signOut = async () => {
    if (reminderKeyRef.current) window.sessionStorage.removeItem(reminderKeyRef.current);
    const signingOutUserId = activeUserIdRef.current;
    sessionEpochRef.current += 1;
    latestProfileRequestRef.current += 1;
    setProfileStatus(null);
    setProfileStatusUserId(null);
    setProfileStatusLoading(false);
    const result = await supabase.auth.signOut();
    if (result?.error && activeUserIdRef.current === signingOutUserId) {
      void requestProfileStatus(signingOutUserId, sessionEpochRef.current);
    }
    return result;
  };

  return (
    <AuthContext.Provider value={{
      user, loading, isAdminUser, signUp, signIn, signOut,
      profileStatus, profileStatusUserId, profileStatusLoading, refreshProfileStatus,
      profileReminderDismissed, dismissProfileReminder,
    }}>
      {children}
    </AuthContext.Provider>
  );
};